import { useArticlesData, useLotsData } from '@gozoki/api';
import {
    Button,
    Chip,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Lot } from '@gozoki/api-types';
import type { QualityArticle } from '@gozoki/api/src/queries/stores.queries';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { GridColDef } from '@mui/x-data-grid';
import SimpleDataGrid from '../../components/containers/SimpleDataGrid';
import Page from '../../components/Page';
import { ROUTE_BUILDER } from '../../utils/constants/routes.constants';
import { CustomFourDatePicker } from '../../utils/dashboard/customFourDatePicker';
import { StoresPicker } from '../../utils/dashboard/storesPicker';
import { ThemeColors } from '../../utils/theme/colors.theme';
import { fonts } from '../../utils/theme/fonts.theme';
import { formatDateOnly } from '../../utils/types/formatDates';
import { formatDateTime } from './qualityUtils';

const LotStatus = ({ lot }: { lot: Lot }) => {
    let title = 'Non bloqué';
    if (lot.blocked) title = 'Bloqué';
    if (!lot.blocked && lot.blockedAt) title = 'Débloqué';
    return <Chip style={{ height: 35 }} label={title} color={lot.blocked ? 'error' : 'success'} />;
};

const qualityColumnDef = (): GridColDef[] => {
    return [
        {
            field: 'lot',
            headerName: 'N° de lot',
            width: 200,
        },
        {
            field: 'ean',
            headerName: 'EAN 13',
            width: 180,
        },
        { field: 'nb', headerName: "Nb d'articles", width: 100 },
        {
            field: 'label',
            headerName: 'Libellé',
            width: 350,
        },
        {
            field: 'dlc',
            headerName: 'DLC',
            valueFormatter: (params) => {
                return formatDateOnly(params.value as string);
            },
            width: 180,
        },
        {
            field: 'tracking',
            headerName: 'Infos de traçabilité',
            width: 200,
        },
        {
            field: 'lo',
            headerName: 'Statut',
            renderCell: (params) => {
                return <LotStatus lot={params.row.lotDetail as Lot} />;
            },
            width: 150,
        },
    ];
};

const articlesColumnDef = (): GridColDef[] => {
    return [
        {
            field: 'checkpointArticleRef',
            headerName: 'Référence',
            width: 220,
        },
        {
            field: 'ean',
            headerName: 'EAN 13',
            width: 180,
        },
        {
            field: 'label',
            headerName: 'Libellé',
            width: 300,
        },
        {
            field: 'dlc',
            headerName: 'DLC',
            valueFormatter: (params) => {
                return formatDateOnly(params.value as string);
            },
            width: 140,
        },
        {
            field: 'storeLabel',
            headerName: 'Boutique',
            width: 150,
        },
        {
            field: 'warehouseAt',
            headerName: 'Plateforme',
            valueFormatter: (params) => formatDateTime(params.value as string),
            width: 160,
        },
        {
            field: 'shippingAt',
            headerName: 'Transport',
            valueFormatter: (params) => formatDateTime(params.value as string),
            width: 160,
        },
        {
            field: 'boutiqueAt',
            headerName: 'Boutique',
            valueFormatter: (params) => formatDateTime(params.value as string),
            width: 160,
        },
        {
            field: 'soldAt',
            headerName: 'Vendu',
            valueFormatter: (params) => formatDateTime(params.value as string),
            width: 160,
        },
        {
            field: 'stolenAt',
            headerName: 'Volé',
            valueFormatter: (params) => formatDateTime(params.value as string),
            width: 160,
        },
        {
            field: 'destroyedAt',
            headerName: 'Détruit',
            valueFormatter: (params) => formatDateTime(params.value as string),
            width: 160,
        },
    ];
};

const QualityPage = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // Tab state - stored in URL as 'tab'
    const [currentTab, setCurrentTab] = useState<number>(
        searchParams.get('tab') ? parseInt(searchParams.get('tab')!, 10) : 0
    );

    // Common filters
    const [referenceSearch, setReferenceSearch] = useState(searchParams.get('ean') || '');
    const [labelSearch, setLabelSearch] = useState(searchParams.get('label') || '');
    const [currentStore, setCurrentStore] = useState<number | null>(
        searchParams.get('store') ? parseInt(searchParams.get('store')!, 10) : null
    );

    // Lots-only filters
    const [lotSearch, setLotSearch] = useState(searchParams.get('lot') || '');
    const [statusFilter, setStatusFilter] = useState<'all' | 'blocked' | 'unblocked'>(
        (searchParams.get('status') as 'all' | 'blocked' | 'unblocked') || 'all'
    );

    // Articles-only filters
    const [articleRefSearch, setArticleRefSearch] = useState(searchParams.get('articleRef') || '');

    // Date filters - DLC and Sold
    const [openDateDialogDlc, setOpenDateDialogDlc] = useState(false);
    const [dateStartDlc, setDateStartDlc] = useState<Date | null>(
        searchParams.get('dateStartDlc') ? new Date(searchParams.get('dateStartDlc')!) : null
    );
    const [dateEndDlc, setDateEndDlc] = useState<Date | null>(
        searchParams.get('dateEndDlc') ? new Date(searchParams.get('dateEndDlc')!) : null
    );

    const [openDateDialogSold, setOpenDateDialogSold] = useState(false);
    const [dateStartSold, setDateStartSold] = useState<Date | null>(
        searchParams.get('dateStartSold') ? new Date(searchParams.get('dateStartSold')!) : null
    );
    const [dateEndSold, setDateEndSold] = useState<Date | null>(
        searchParams.get('dateEndSold') ? new Date(searchParams.get('dateEndSold')!) : null
    );

    // Pagination
    const [page, setPage] = useState<number>(
        searchParams.get('page') ? parseInt(searchParams.get('page')!, 10) : 0
    );
    const [pageSize, setPageSize] = useState(
        searchParams.get('pageSize') ? parseInt(searchParams.get('pageSize')!, 10) : 20
    );

    // Update URL params when any filter changes
    useEffect(() => {
        const params: Record<string, string> = {};
        if (currentTab !== 0) params.tab = currentTab.toString();
        if (referenceSearch) params.ean = referenceSearch;
        if (labelSearch) params.label = labelSearch;
        if (currentStore !== null) params.store = currentStore.toString();
        if (currentTab === 0) {
            // Lots tab
            if (lotSearch) params.lot = lotSearch;
            if (statusFilter !== 'all') params.status = statusFilter;
        } else if (currentTab === 1) {
            // Articles tab
            if (articleRefSearch) params.articleRef = articleRefSearch;
        }
        if (dateStartDlc) params.dateStartDlc = dateStartDlc.toISOString();
        if (dateEndDlc) params.dateEndDlc = dateEndDlc.toISOString();
        if (dateStartSold) params.dateStartSold = dateStartSold.toISOString();
        if (dateEndSold) params.dateEndSold = dateEndSold.toISOString();
        if (page !== 0) params.page = page.toString();
        if (pageSize !== 20) params.pageSize = pageSize.toString();

        // We use replace: true so navigating between filters doesn't pollute the browser history
        // (prevents "back" from stepping through every filter change)
        setSearchParams(params, { replace: true });
    }, [
        currentTab,
        lotSearch,
        referenceSearch,
        labelSearch,
        currentStore,
        statusFilter,
        articleRefSearch,
        dateStartDlc,
        dateEndDlc,
        dateStartSold,
        dateEndSold,
        page,
        pageSize,
        setSearchParams,
    ]);

    const {
        data: lots,
        isLoading: isLoadingLots,
        isFetching: isFetchingLots,
    } = useLotsData(
        page + 1, // x-data-grid starts at 0 but lucid starts at 1
        pageSize,
        lotSearch,
        referenceSearch,
        labelSearch,
        currentStore,
        false,
        statusFilter,
        dateStartSold,
        dateEndSold,
        dateStartDlc,
        dateEndDlc
    );

    const {
        data: articles,
        isLoading: isLoadingArticles,
        isFetching: isFetchingArticles,
    } = useArticlesData(
        page + 1,
        pageSize,
        articleRefSearch,
        referenceSearch,
        labelSearch,
        currentStore,
        dateStartSold,
        dateEndSold,
        dateStartDlc,
        dateEndDlc
    );

    const safeLots: Lot[] = useMemo(() => lots?.data ?? [], [lots]);
    const safeArticles: QualityArticle[] = useMemo(() => articles?.data ?? [], [articles]);

    const handleLotClick = (lot: Lot) => {
        navigate(ROUTE_BUILDER.lotDetails(lot.lot, lot.ean, lot.tracking, lot.dlc));
    };

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setCurrentTab(newValue);
        setPage(0); // Reset pagination when switching tabs
    };

    return (
        <Page>
            <div style={{ display: 'flex', paddingLeft: 10, marginBottom: 10 }}>
                <Typography style={fonts.pageTitle} flexGrow={1}>
                    Traçabilité & Qualité
                </Typography>
            </div>

            <Tabs value={currentTab} onChange={handleTabChange}>
                <Tab label="Recherche par lot" />
                <Tab label="Recherche par article" />
            </Tabs>

            <Paper
                sx={{
                    marginTop: '16px',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '16px',
                        flex: 3,
                        flexWrap: 'wrap',
                    }}
                >
                    {currentTab === 0 && (
                        <div style={{ padding: 16 }}>
                            <TextField
                                variant="outlined"
                                label="Recherche par n° de lot"
                                value={lotSearch}
                                onChange={(e) => setLotSearch(e.target.value ?? '')}
                            />
                        </div>
                    )}
                    {currentTab === 1 && (
                        <div style={{ padding: 16 }}>
                            <TextField
                                variant="outlined"
                                label="Recherche par référence"
                                value={articleRefSearch}
                                onChange={(e) => setArticleRefSearch(e.target.value ?? '')}
                            />
                        </div>
                    )}
                    <div style={{ padding: 16 }}>
                        <TextField
                            variant="outlined"
                            label="Recherche par EAN 13"
                            value={referenceSearch}
                            onChange={(e) => setReferenceSearch(e.target.value)}
                        />
                    </div>
                    <div style={{ padding: 16 }}>
                        <TextField
                            variant="outlined"
                            label="Recherche par libellé"
                            value={labelSearch}
                            onChange={(e) => setLabelSearch(e.target.value)}
                        />
                    </div>
                    <div style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Button
                            variant="contained"
                            onClick={() => setOpenDateDialogSold(true)}
                            style={{
                                height: 55,
                                backgroundColor:
                                    dateStartSold && dateEndSold
                                        ? ThemeColors.LIGHT_ORANGE
                                        : ThemeColors.WHITE,
                                color:
                                    dateStartSold && dateEndSold
                                        ? ThemeColors.ORANGE
                                        : ThemeColors.GREY_900,
                            }}
                        >
                            {dateStartSold && dateEndSold
                                ? `${formatDateOnly(dateStartSold)} - ${formatDateOnly(
                                      dateEndSold
                                  )}`
                                : 'Date de vente'}
                        </Button>
                        {currentTab === 0 && (
                            <Tooltip
                                title="Filtrer les lots par la date de vente des articles (lost_at). Seuls les lots contenant au moins un article vendu dans la période sélectionnée seront affichés."
                                arrow
                                placement="top"
                            >
                                <InfoOutlinedIcon
                                    sx={{
                                        color: ThemeColors.GREY_900,
                                        fontSize: 20,
                                        cursor: 'help',
                                    }}
                                />
                            </Tooltip>
                        )}
                        {dateStartSold && dateEndSold && (
                            <Button
                                onClick={() => {
                                    setDateStartSold(null);
                                    setDateEndSold(null);
                                }}
                                style={{ marginLeft: 8 }}
                            >
                                Réinitialiser
                            </Button>
                        )}
                    </div>
                    <div style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Button
                            variant="contained"
                            onClick={() => setOpenDateDialogDlc(true)}
                            style={{
                                height: 55,
                                backgroundColor:
                                    dateStartDlc && dateEndDlc
                                        ? ThemeColors.LIGHT_ORANGE
                                        : ThemeColors.WHITE,
                                color:
                                    dateStartDlc && dateEndDlc
                                        ? ThemeColors.ORANGE
                                        : ThemeColors.GREY_900,
                            }}
                        >
                            {dateStartDlc && dateEndDlc
                                ? `${formatDateOnly(dateStartDlc)} - ${formatDateOnly(dateEndDlc)}`
                                : 'DLC'}
                        </Button>
                        {currentTab === 0 && (
                            <Tooltip
                                title="Filtrer les lots par la date limite de consommation (DLC) des articles. Seuls les lots contenant au moins un article dont la DLC est dans la période sélectionnée seront affichés."
                                arrow
                                placement="top"
                            >
                                <InfoOutlinedIcon
                                    sx={{
                                        color: ThemeColors.GREY_900,
                                        fontSize: 20,
                                        cursor: 'help',
                                    }}
                                />
                            </Tooltip>
                        )}
                        {dateStartDlc && dateEndDlc && (
                            <Button
                                onClick={() => {
                                    setDateStartDlc(null);
                                    setDateEndDlc(null);
                                }}
                                style={{ marginLeft: 8 }}
                            >
                                Réinitialiser
                            </Button>
                        )}
                    </div>
                    {currentTab === 0 && (
                        <div style={{ padding: 16, minWidth: 200 }}>
                            <FormControl fullWidth>
                                <InputLabel>Statut</InputLabel>
                                <Select
                                    label="Statut"
                                    value={statusFilter}
                                    onChange={(e) =>
                                        setStatusFilter(
                                            e.target.value as 'all' | 'blocked' | 'unblocked'
                                        )
                                    }
                                >
                                    <MenuItem value="all">Tous</MenuItem>
                                    <MenuItem value="blocked">Bloqué</MenuItem>
                                    <MenuItem value="unblocked">Non bloqué</MenuItem>
                                </Select>
                            </FormControl>
                        </div>
                    )}
                    <div style={{ flex: 1, minWidth: 200 }}>
                        <StoresPicker
                            setUniqueStore={setCurrentStore}
                            allowAllStores
                            onlyBoutiques
                            defaultStore={currentStore !== null ? currentStore : undefined}
                        />
                    </div>
                </div>
            </Paper>

            <CustomFourDatePicker
                openDialog={openDateDialogDlc}
                setOpenDialog={setOpenDateDialogDlc}
                dateState={{
                    timeRanges: {
                        all: [],
                        current: 0,
                        set: () => {},
                    },
                    currentDate: {
                        start: dateStartDlc || new Date(),
                        setStart: setDateStartDlc,
                        end: dateEndDlc || new Date(),
                        setEnd: setDateEndDlc,
                    },
                }}
            />

            <CustomFourDatePicker
                openDialog={openDateDialogSold}
                setOpenDialog={setOpenDateDialogSold}
                dateState={{
                    timeRanges: {
                        all: [],
                        current: 0,
                        set: () => {},
                    },
                    currentDate: {
                        start: dateStartSold || new Date(),
                        setStart: setDateStartSold,
                        end: dateEndSold || new Date(),
                        setEnd: setDateEndSold,
                    },
                }}
            />

            <Paper
                sx={{
                    marginTop: '16px',
                }}
            >
                {currentTab === 0 ? (
                    <SimpleDataGrid
                        columns={qualityColumnDef()}
                        rows={safeLots.map((l) => {
                            return {
                                id: `${page}-${l.lot}-${l.ean}-${l.dlc}-${l.comment}-${l.tracking}-${l.blocked}`,
                                lot: l.lot,
                                ean: l.ean,
                                nb: l.nb,
                                label: l.label,
                                dlc: l.dlc,
                                tracking: l.tracking,
                                lotDetail: l,
                            };
                        })}
                        loading={isLoadingLots || isFetchingLots}
                        rowCount={lots?.meta.total ?? 0}
                        pageSizeOptions={[20, 50]}
                        paginationMode="server"
                        paginationModel={{ page, pageSize }}
                        onPaginationModelChange={(params) => {
                            setPage(params.page);
                            setPageSize(params.pageSize);
                        }}
                        disableRowSelectionOnClick
                        checkboxSelection={false}
                        onRowClick={(row) => {
                            if (row.row.lotDetail) handleLotClick(row.row.lotDetail as Lot);
                        }}
                    />
                ) : (
                    <SimpleDataGrid
                        columns={articlesColumnDef()}
                        rows={safeArticles.map((a) => {
                            return {
                                id: a.checkpointArticleRef,
                                checkpointArticleRef: a.checkpointArticleRef,
                                ean: a.ean,
                                label: a.label,
                                dlc: a.dlc,
                                storeLabel: a.storeLabel,
                                warehouseAt: a.warehouseAt,
                                shippingAt: a.shippingAt,
                                boutiqueAt: a.boutiqueAt,
                                soldAt: a.soldAt,
                                stolenAt: a.stolenAt,
                                destroyedAt: a.destroyedAt,
                            };
                        })}
                        loading={isLoadingArticles || isFetchingArticles}
                        rowCount={articles?.meta.total ?? 0}
                        pageSizeOptions={[20, 50]}
                        paginationMode="server"
                        paginationModel={{ page, pageSize }}
                        onPaginationModelChange={(params) => {
                            setPage(params.page);
                            setPageSize(params.pageSize);
                        }}
                        disableRowSelectionOnClick
                        checkboxSelection={false}
                    />
                )}
            </Paper>
        </Page>
    );
};

export default QualityPage;
